/**
 * Article page enhancements
 * ---------------------------------------------------------------------------
 * Table of contents, reading progress and copy-link. All progressive: the
 * article reads perfectly with this script blocked.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---- Table of contents ---------------------------------------------------
 * Built from the rendered H2s rather than a hand-maintained list, so it stays
 * correct no matter how the merchant edits the post in the Shopify editor.
 */
function buildToc() {
  const toc = document.querySelector('[data-art-toc]');
  const list = document.querySelector('[data-art-toc-list]');
  const content = document.querySelector('[data-art-content]');
  if (!toc || !list || !content) return;

  const headings = Array.from(content.querySelectorAll('h2'));

  // A contents list for one or two sections is noise, not navigation.
  if (headings.length < 3) return;

  const used = new Set();

  headings.forEach((heading, index) => {
    if (!heading.id) {
      const base =
        heading.textContent
          .trim()
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .slice(0, 60) || `section-${index + 1}`;

      // Guarantee uniqueness — duplicate headings are common.
      let id = base;
      let n = 2;
      while (used.has(id) || document.getElementById(id)) {
        id = `${base}-${n}`;
        n += 1;
      }
      heading.id = id;
    }
    used.add(heading.id);

    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    item.appendChild(link);
    list.appendChild(item);
  });

  toc.hidden = false;

  // Highlight the section currently in view.
  if (!('IntersectionObserver' in window)) return;

  const links = new Map(
    Array.from(list.querySelectorAll('a')).map((a) => [a.getAttribute('href').slice(1), a])
  );

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        links.forEach((link) => link.setAttribute('aria-current', 'false'));
        links.get(entry.target.id)?.setAttribute('aria-current', 'true');
      });
    },
    // Top-third band: marks a heading active once it reaches reading position.
    { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
  );

  headings.forEach((heading) => observer.observe(heading));
}

/* ---- Reading progress ---------------------------------------------------- */
function setupProgress() {
  const bar = document.querySelector('[data-art-progress] span');
  const content = document.querySelector('[data-art-content]');
  if (!bar || !content) return;

  let ticking = false;

  const update = () => {
    // Progress is measured across the article body, not the whole document —
    // otherwise the footer makes it read "80% done" while still mid-article.
    const rect = content.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const scrolled = -rect.top;
    const percent = total <= 0 ? 100 : Math.min(100, Math.max(0, (scrolled / total) * 100));
    bar.style.width = `${percent}%`;
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
}

/* ---- Copy link ----------------------------------------------------------- */
function setupCopy() {
  document.querySelectorAll('[data-art-copy]').forEach((button) => {
    button.addEventListener('click', async () => {
      const url = button.dataset.url || window.location.href;
      const original = button.textContent;

      try {
        await navigator.clipboard.writeText(url);
        button.textContent = 'Copied ✓';
      } catch {
        // Clipboard API needs a secure context and can be blocked entirely.
        const field = document.createElement('input');
        field.value = url;
        document.body.appendChild(field);
        field.select();
        try {
          document.execCommand('copy');
          button.textContent = 'Copied ✓';
        } catch {
          button.textContent = 'Press Ctrl+C';
        }
        field.remove();
      }

      setTimeout(() => {
        button.textContent = original;
      }, 2000);
    });
  });
}

/* ---- Smooth anchor scrolling --------------------------------------------- */
function setupSmoothAnchors() {
  document.querySelectorAll('[data-art-toc-list] a').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
      // Keep the URL shareable and the back button meaningful.
      history.pushState(null, '', `#${id}`);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  });
}

function init() {
  buildToc();
  setupProgress();
  setupCopy();
  setupSmoothAnchors();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
